import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilesService } from '../../core/files.service';
import { I18nService } from '../../core/i18n.service';
import { UserFile } from '../../core/models';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';

@Component({ selector: 'app-my-files', standalone: true, imports: [FormsModule, LoadingSpinner], templateUrl: './my-files.html', styleUrl: './my-files.scss' })
export class MyFiles {
  readonly fileTypes: UserFile['type'][] = ['CV', 'Cover letter', 'Recommendation letter', 'Transcript of records', 'Other'];
  readonly modal = { isOpen: false, type: 'CV' as UserFile['type'], title: '', note: '', file: null as File | null };
  isSubmitting = false;

  constructor(public files: FilesService, public i18n: I18nService) {}

  fileTypeLabel(type: UserFile['type']): string {
    const key = {
      CV: 'files.types.cv',
      'Cover letter': 'files.types.cover',
      'Recommendation letter': 'files.types.recommendation',
      'Transcript of records': 'files.types.transcript',
      Other: 'files.types.other'
    }[type];
    return this.i18n.t(key);
  }

  fileStatusLabel(status: UserFile['status']): string {
    const key = {
      Draft: 'files.statuses.draft',
      Review: 'files.statuses.review',
      Ready: 'files.statuses.ready'
    }[status];
    return this.i18n.t(key);
  }

  updatedAtLabel(updatedAt: UserFile['updatedAt']): string {
    const key = {
      Today: 'files.updated.today',
      Yesterday: 'files.updated.yesterday',
      'Last week': 'files.updated.lastWeek',
      Now: 'files.updated.now'
    }[updatedAt];
    return key ? this.i18n.t(key) : this.i18n.formatDate(updatedAt.slice(0, 10));
  }

  openNewFileModal(): void {
    if (this.files.isBusy('add')) return;

    this.modal.isOpen = true;
    this.modal.type = 'CV';
    this.modal.title = '';
    this.modal.note = '';
    this.modal.file = null;
  }

  closeNewFileModal(): void {
    if (this.isSubmitting) return;
    this.modal.isOpen = false;
  }

  onLocalFileSelected(event: Event): void {
    if (this.isSubmitting) return;

    const input = event.target as HTMLInputElement;
    this.modal.file = input.files?.[0] ?? null;
    if (this.modal.file && !this.modal.title.trim()) {
      this.modal.title = this.modal.file.name.replace(/\.[^.]+$/, '');
    }
  }

  async submitNewFile(): Promise<void> {
    if (!this.modal.file || !this.modal.title.trim() || this.isSubmitting) return;
    this.isSubmitting = true;
    try {
      const contentBase64 = await this.fileToBase64(this.modal.file);
      const created = await this.files.add({
        type: this.modal.type,
        title: this.modal.title.trim(),
        status: 'Ready',
        note: this.modal.note,
        originalFilename: this.modal.file.name,
        contentType: this.modal.file.type || 'application/octet-stream',
        contentBase64
      });
      if (created) this.modal.isOpen = false;
    } finally {
      this.isSubmitting = false;
    }
  }

  async onReplacementSelected(fileRecord: UserFile, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const replacement = input.files?.[0];
    if (!replacement) return;
    try {
      await this.files.replaceContent(fileRecord, {
        originalFilename: replacement.name,
        contentType: replacement.type || 'application/octet-stream',
        contentBase64: await this.fileToBase64(replacement)
      });
    } finally {
      input.value = '';
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Unable to read selected file.'));
      reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
      reader.readAsDataURL(file);
    });
  }
}
